/**
 * Google Analytics 4 — minimal native gtag.js helper.
 *
 * GA4 measurement ID is resolved in this order:
 *   1. process.env.REACT_APP_GA4_ID          (build-time, requires rebuild)
 *   2. window.__PETFOLK_CONFIG?.ga4_id        (runtime, set by index.html if needed)
 *   3. GET /api/config/public { ga4_id }      (runtime, NO rebuild required)
 *
 * This means production can flip GA4 on/off purely by setting the env var
 * on the backend container — no frontend rebuild needed.
 *
 * Tracked events:
 *  - page_view                (auto on route change)
 *  - login                    { method, user_role }
 *  - sign_up                  { method, user_role }
 *  - store_view               { store_slug, store_name, city }
 *  - booking_started          { service_id, store_slug, species }
 *  - booking_confirmed        { booking_id, value, currency, service_id }
 *  - franchise_lead_submit    { city, format, budget }
 *  - subscription_purchased   { plan_name, value }
 *  - referral_shared          { method }
 *  - auth_otp_sent            { channel: 'sms' | 'email' }
 *  - blog_list_view           { category }
 *  - blog_view                { blog_slug, blog_category, blog_title }
 *  - blog_share               { blog_slug, method }
 *  - blog_cta_click           { blog_slug, cta_name }
 *  - blog_published           { blog_slug, from_status, to_status }
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const ENV_GA4_ID = (process.env.REACT_APP_GA4_ID || '').trim();
const DEBUG = process.env.NODE_ENV !== 'production';

let _resolvedId = null;
let _loaded = false;
let _initPromise = null;
let _pendingPageView = null;
let _pendingUser = null;
const _pendingEvents = [];

function isValidGa4Id(id) {
  return typeof id === 'string' && /^G-[A-Z0-9]+$/i.test(id.trim());
}

/** Resolve GA4 measurement ID from env → window override → backend /api/config/public. */
async function resolveGa4Id() {
  if (_resolvedId !== null) return _resolvedId;

  // 1) build-time env var
  if (isValidGa4Id(ENV_GA4_ID)) {
    _resolvedId = ENV_GA4_ID;
    if (DEBUG) console.info('[GA4] using env var:', _resolvedId);
    return _resolvedId;
  }

  // 2) window-injected override (lets ops set via index.html script tag)
  if (typeof window !== 'undefined' && window.__PETFOLK_CONFIG?.ga4_id) {
    const id = String(window.__PETFOLK_CONFIG.ga4_id).trim();
    if (isValidGa4Id(id)) {
      _resolvedId = id;
      if (DEBUG) console.info('[GA4] using window config:', _resolvedId);
      return _resolvedId;
    }
  }

  // 3) backend runtime config
  if (BACKEND_URL) {
    try {
      const res = await fetch(`${BACKEND_URL}/api/config/public`, { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        if (data?.ga4_id && isValidGa4Id(data.ga4_id)) {
          _resolvedId = data.ga4_id;
          if (DEBUG) console.info('[GA4] using runtime config from backend:', _resolvedId);
          return _resolvedId;
        }
      }
    } catch (e) {
      if (DEBUG) console.warn('[GA4] runtime config fetch failed:', e?.message);
    }
  }

  _resolvedId = '';
  if (DEBUG) console.info('[GA4] disabled — no measurement ID found.');
  return _resolvedId;
}

function loadGtag(id) {
  if (_loaded || !id || typeof window === 'undefined') return;
  _loaded = true;

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  s.onerror = () => { if (DEBUG) console.warn('[GA4] gtag.js failed to load (ad-blocker?)'); };
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', id, {
    anonymize_ip: true,
    send_page_view: false, // SPA — we send manually on route change
  });
  if (DEBUG) console.info('[GA4] gtag initialized for', id);

  // Flush buffered events (page_view, user, custom events fired before init resolved)
  if (_pendingUser) {
    const { userId, role } = _pendingUser;
    window.gtag('set', { user_id: userId || undefined });
    if (role) window.gtag('set', 'user_properties', { user_role: role });
    _pendingUser = null;
  }
  if (_pendingPageView) {
    window.gtag('event', 'page_view', {
      page_path: _pendingPageView.pagePath,
      page_title: _pendingPageView.pageTitle || document.title,
      page_location: typeof window !== 'undefined' ? window.location.href : undefined,
    });
    _pendingPageView = null;
  }
  while (_pendingEvents.length > 0) {
    const { name, params } = _pendingEvents.shift();
    window.gtag('event', name, params);
  }
}

/** Initialize gtag.js exactly once. Async because runtime config may need a fetch. Safe to call multiple times. */
export function initGA4() {
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    const id = await resolveGa4Id();
    loadGtag(id);
  })();
  return _initPromise;
}

/** Manual SPA page_view */
export function trackPageView(pagePath, pageTitle) {
  if (!_loaded || !window.gtag) {
    // Buffer the most recent page_view until init resolves
    _pendingPageView = { pagePath, pageTitle };
    return;
  }
  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
    page_location: typeof window !== 'undefined' ? window.location.href : undefined,
  });
}

/** Set the GA4 user_id once a user is authenticated. Pass null to clear. */
export function setUser(userId, role) {
  if (!_loaded || !window.gtag) {
    _pendingUser = { userId, role };
    return;
  }
  window.gtag('set', { user_id: userId || undefined });
  if (role) {
    window.gtag('set', 'user_properties', { user_role: role });
  }
}

/** Generic custom event */
export function trackEvent(name, params = {}) {
  if (!_loaded || !window.gtag) {
    _pendingEvents.push({ name, params });
    if (_pendingEvents.length > 50) _pendingEvents.shift(); // safety cap
    return;
  }
  window.gtag('event', name, params);
}

// --- Convenience wrappers (named events) ---
export const ga = {
  login: (method, userRole) => trackEvent('login', { method, user_role: userRole }),
  signUp: (method, userRole) => trackEvent('sign_up', { method, user_role: userRole }),
  storeView: (slug, name, city) => trackEvent('store_view', { store_slug: slug, store_name: name, city }),
  bookingStarted: (serviceId, storeSlug, species) =>
    trackEvent('booking_started', { service_id: serviceId, store_slug: storeSlug, species }),
  bookingConfirmed: (bookingId, value, serviceId) =>
    trackEvent('booking_confirmed', { booking_id: bookingId, value, currency: 'INR', service_id: serviceId }),
  franchiseLead: (city, format, budget) =>
    trackEvent('franchise_lead_submit', { city, format, budget, currency: 'INR' }),
  subscription: (planName, value) =>
    trackEvent('subscription_purchased', { plan_name: planName, value, currency: 'INR' }),
  referralShared: (method) => trackEvent('referral_shared', { method }),
  otpSent: (channel) => trackEvent('auth_otp_sent', { channel }),
  // Blog / CMS
  blogListView: (category) => trackEvent('blog_list_view', { category: category || 'all' }),
  blogView: (slug, category, title) => trackEvent('blog_view', { blog_slug: slug, blog_category: category, blog_title: title }),
  blogShare: (slug, method) => trackEvent('blog_share', { blog_slug: slug, method }),
  blogCtaClick: (slug, ctaName) => trackEvent('blog_cta_click', { blog_slug: slug, cta_name: ctaName || 'find_petfolk' }),
  blogPublished: (slug, fromStatus, toStatus) =>
    trackEvent('blog_published', { blog_slug: slug, from_status: fromStatus, to_status: toStatus }),
};

// Expose a one-shot diagnostic helper for the browser console
if (typeof window !== 'undefined') {
  window.__petfolkGa4Status = () => ({
    resolvedId: _resolvedId,
    loaded: _loaded,
    hasGtag: typeof window.gtag === 'function',
    dataLayerSize: (window.dataLayer || []).length,
  });
}
